<script setup lang="ts">
import Dialog from "primevue/dialog";
import factionData from "../faction_data.json";

const armyListModal = ref(false);

function modalShow() {
  history.pushState(
    { path: window.location.search },
    "",
    "/list/" + selectedPlayer.value.player_bcp_id
  );
}

function modalHidden() {
  if (history.state?.path) history.pushState({}, "", "/" + history.state.path);
}

const playerTable = ref();
const selectedPlayer = ref();

const route = useRoute();
const selectedLosses = ref(String(route.query.losses || "1"));
const selectedFaction = ref(String(route.query.faction || ""));
const selectedDetachment = ref(String(route.query.detachment || ""));
const selectedRounds = ref(String(route.query.rounds || "all"));
const selectedOffset = ref(parseInt(String(route.query.offset)) || 0);
const selectedPlayerCount = ref(String(route.query.player_count || "all"));

const router = useRouter();

// The ?. is for a bug in vue
window?.addEventListener("popstate", () => {
  if (armyListModal.value) {
    armyListModal.value = false;
  }
  return true;
});
watch(
  [
    selectedLosses,
    selectedFaction,
    selectedDetachment,
    selectedRounds,
    selectedOffset,
    selectedPlayerCount,
  ],
  () => {
    router.push({
      query: {
        losses: selectedLosses.value,
        faction: selectedFaction.value,
        detachment: selectedDetachment.value,
        rounds: selectedRounds.value,
        offset: selectedOffset.value,
        player_count: selectedPlayerCount.value,
      },
    });
  }
);

const app = useNuxtApp();
const { data: players, pending } = await useFetch("/api/search", {
  query: {
    losses: selectedLosses,
    faction: selectedFaction,
    detachment: selectedDetachment,
    rounds: selectedRounds,
    offset: selectedOffset,
    player_count: selectedPlayerCount,
  },
});

watch(selectedFaction, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    selectedDetachment.value = "";
  }
});
</script>

<template>
  <div class="columns">
    <div class="loading-overlay is-overlay" v-if="pending">
      <progress-spinner />
    </div>
    <div class="column is-3">
      <form>
        <b>Faction</b>
        <br />
        <div class="select">
          <select name="faction" v-model="selectedFaction">
            <option value="" selected>All Factions</option>
            <option v-for="f in Object.keys(factionData).sort()" :key="f">
              {{ f }}
            </option>
          </select>
        </div>
        <br />
        <b style="font-size: 0.9rem">Detachment</b>
        <br />
        <div class="select">
          <select name="faction" v-model="selectedDetachment">
            <option value="">All Detachments</option>
            <option v-for="d in factionData[selectedFaction]?.sort()" :key="d">
              {{ d }}
            </option>
          </select>
        </div>
        <div class="control">
          <b>Wins</b>
          <ul>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedLosses" :value="'0'" />
                Undefeated
              </label>
            </li>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedLosses" :value="'1'" />
                1 or fewer Losses
              </label>
            </li>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedLosses" :value="'2'" />
                2 or fewer Losses
              </label>
            </li>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedLosses" :value="'all'" />
                All
              </label>
            </li>
          </ul>
        </div>
        <div class="control">
          <b>Rounds</b>
          <ul>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedRounds" :value="'4'" />
                5 or more
              </label>
            </li>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedRounds" :value="'6'" />
                6 or more
              </label>
            </li>
            <li>
              <label class="radio">
                <input type="radio" v-model="selectedRounds" :value="'all'" />
                All
              </label>
            </li>
          </ul>
        </div>
        <div class="control">
          <b>Players</b>
          <ul>
            <li>
              <label class="radio">
                <input
                  type="radio"
                  v-model="selectedPlayerCount"
                  :value="'10'"
                />
                10 or more
              </label>
            </li>
            <li>
              <label class="radio">
                <input
                  type="radio"
                  v-model="selectedPlayerCount"
                  :value="'30'"
                />
                30 or more
              </label>
            </li>
            <li>
              <label class="radio">
                <input
                  type="radio"
                  v-model="selectedPlayerCount"
                  :value="'60'"
                />
                60 or more
              </label>
            </li>
            <li>
              <label class="radio">
                <input
                  type="radio"
                  v-model="selectedPlayerCount"
                  :value="'all'"
                />
                All
              </label>
            </li>
          </ul>
        </div>
      </form>
    </div>
    <div class="column is-9">
      <h1 class="h1">Search Results</h1>
      <table class="table is-striped is-hoverable mainplayer" ref="playerTable">
        <thead>
          <tr>
            <th>Faction</th>
            <th>Player</th>
            <th>Event</th>
            <th>Date</th>
            <th>Record</th>
            <th>Players</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="player in players"
            :key="player.player_bcp_id"
            @click="
              armyListModal = true;
              selectedPlayer = player;
            "
          >
            <td>
              {{ player.parsed_faction }}
            </td>
            <td>
              <a :href="'/list/' + player.player_bcp_id" target="_blank">{{
                player.player_name
              }}</a>
            </td>
            <td>
              <NuxtLink
                :to="`/event/${player.event_id}`"
                @click="$event.stopPropagation()"
                >{{ player.event_name }}</NuxtLink
              >
            </td>
            <td style="white-space: nowrap">{{ player.event_date }}</td>
            <td style="white-space: nowrap">
              {{ player.wins }} -
              {{ player.losses }}
              {{ player.draws > 0 ? " - " + player.draws : "" }}
            </td>
            <td>
              {{ player.players }}
            </td>
          </tr>
        </tbody>
      </table>
      <div>
        <button class="button is-primary" @click="selectedOffset = 0">
          First
        </button>
        <button
          class="button is-primary"
          @click="
            playerTable.scrollIntoView({ behavior: 'smooth' });
            selectedOffset += 50;
          "
        >
          Next
        </button>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="armyListModal"
    modal
    dismissableMask
    blockScroll
    :draggable="false"
    :header="selectedPlayer?.player_name + ' - ' + selectedPlayer?.event_name"
    @after-hide="modalHidden()"
    @show="modalShow()"
  >
    <ListView :player="selectedPlayer" />
  </Dialog>
</template>

<style>
h1 {
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: 500;
}

.mainplayer tbody tr {
  cursor: pointer;
}

thead td {
  font-size: 1.1em;
}

.loading-overlay {
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 9999;
}

.loading-overlay .p-progress-spinner {
  width: 200px;
  height: 200px;
  left: 50%;
}
</style>
